import { appstr } from "../utils/appstr";
import { CreateArrayRegExpression } from "../utils/CreateArrayRegExpression";
import {
  enumCalcCode,
  enumFieldType,
  enumFilterModes,
  enumFormulaScope,
  enumHierarchyTypes,
  enumLockStatus,
  enumOverrideType,
  enumPersistStatus,
  restrictedInternalFields,
  enumObjectTypes,
  enumObjClassNames,
  objTypeClassMap,
  AssignmentStatus,
  kEmployeePosition,
  kAssignmentStatus,
  kPositionStatus,
  PositionStatus,
  kEmployeePositionID,
  kPersonID,
  kTitleID,
  kPositionID,
  kPositionTitleID,
  kBlockedFieldTag,
  kAgentMarkers
} from "../constants";
import {
  isBlockedField,
  getBlockedFieldDisplayValue,
  sanitizeBlockedValue
} from "../utils/FormatFieldValue";
import { ExcludeFromCompress } from "../utils/ExcludeFromCompress";
import { FormatEmployeeLabelValue } from "../utils/FormatEmployeeLabelValue";
import { isInPlanning } from "../utils/isInPlanning";
import { QChange } from "./QChange";
import { QGenericIndex } from "./QGenericIndex";
import { QObjDBFieldValue } from "./QObjDBFieldValue";
import { QSerialObjDBObject } from "./QSerialObjDBObject";
import { QXMLObject } from "./QXMLObject";
import { SmartGetFieldValue } from "../utils/SmartGetFieldValue";
import { SysGen } from "../utils/SysGen";
import { valueCount } from "../utils/valueCount";
import { Globals } from "./Globals";
import { minMaxValue } from "../utils/minMaxValue.js";
import { NineBoxCode } from "../utils/NineBoxCode.js";
import { CreateRegExpression } from "../utils/CreateRegExpression.js";

/**
 * @typedef {import('./QObjDBClass').QObjDBClass} QObjDBClass
 * @typedef {import('./QObjDBFieldDef').QObjDBFieldDef} QObjDBFieldDef
 */

export class QObjDBObject extends QXMLObject {
  constructor() {
    super();
    /**
     * @private
     * @type {(null | QObjDBClass)}
     */
    this.fParentClass = null;
    this.fFieldValues = null;
    this.fParent = null;
    this.fChildren = null;
    this.fRollUp = false;
    /**
     * @private
     * @type {(null | QObjDBObject)}
     */
    this.fParentPosition = null;
    /**
     * @private
     * @type {(null | QObjDBObject[])}
     */
    this.fHolderList = null;
    this.fTempParentID = null;
    this.fTempChildIndex = null;
    this.fCloneBase = null;
    this.fSortKey = null;
    this.fPersist = true;
    this.fFieldValIndex = null;
    this.count = null;
    this.sum = null;
    this.fFiltered = false;
    this.fMuted = false;

    this.fRecordDetails = {};
  }

  CheckIsInChainOfCommand(curSubchart) {
    var curTopbox = curSubchart.GetTopDBObj();
    if (!curTopbox) return false;

    var parent = curTopbox.GetParent();
    while (parent) {
      if (parent == this) return true;
      parent = parent.GetParent();
    }
    return false;
  }

  // Filtered, is a manager, and include filtered managers is enabled at the filterMgr level.
  // To be used for bypassing filtered checks when it comes to removing boxes.
  // Still want to keep the fFiltered state unaltered in this case (in order to add the box overlay)
  IncludedFilteredManager() {
    const p = this.GetParentClass();
    const docRef = p.GetDocRef();
    const isFiltered = this.fFiltered;
    const isManager = this.CheckHasUnfilteredDirectReports();
    const filterMgr = docRef.GetFilterMgr();
    const showFilteredManagers = filterMgr.GetFilterMode() === enumFilterModes.includefilteredmgrs;

    return isManager && showFilteredManagers && isFiltered;
  }

  // TODO: Revise this logic when internal calc fields are no longer the deciding factor for scArrows.
  //  'has direct reports' and 'has shown direct reports' are two distinct concepts that will need to be addressed.
  GetHasDirectReports() {
    // For position objects, use Direct Subordinate Positions; for employee objects, use Span Of Control
    if (this.IsPositionObj && this.IsPositionObj()) {
      return this.SmartGetFieldValue("Direct Subordinate Positions") > 0;
    }
    return this.SmartGetFieldValue("Span Of Control") > 0;
  }

  CheckHasUnfilteredDirectReports() {
    // check filtered list first
    const childList = this.GetChildList();
    if (childList && childList.length > 0) {
      return true;
    }

    // if all reports are filtered, check if these have unfiltered direct reports
    const allChildrenList = this.GetChildListNoFilter();
    if (allChildrenList && allChildrenList.length > 0) {
      for (let i = 0; i < allChildrenList.length; i++) {
        if (allChildrenList[i].CheckHasUnfilteredDirectReports()) {
          return true;
        }
      }
    }

    return false;
  }

  CheckParentFiltered(scm, fullList, subChartID) {
    var parentObj = this.GetParent();

    // In Pos charts, we want to take the Parent box
    if (!parentObj && this.fParentPosition) parentObj = this.fParentPosition.GetParent();
    var curSubchart = scm.FindSubChart(subChartID);
    var fullDBObjList = fullList.map(function (aNode) {
      return aNode.GetDataObj();
    });
    while (parentObj) {
      if (parentObj && parentObj.IsFiltered()) {
        var parentIdx = fullDBObjList.indexOf(parentObj);
        if (parentIdx > -1) {
          if (parentObj.CheckIsInChainOfCommand(curSubchart)) return false;
          else if (parentObj != curSubchart.GetTopDBObj()) return true;
        }
      }
      parentObj = parentObj.GetParent();
    }
    return false;
  }

  // Optimized version that accepts a pre-computed Set of data objects and the already-resolved subchart.
  // Avoids O(n) .map() + .indexOf() per call — uses O(1) Set.has() instead.
  CheckParentFilteredFast(scm, fullDBObjSet, subChartID, curSubchart) {
    var parentObj = this.GetParent();

    // In Pos charts, we want to take the Parent box
    if (!parentObj && this.fParentPosition) parentObj = this.fParentPosition.GetParent();
    if (!curSubchart) curSubchart = scm.FindSubChart(subChartID);
    while (parentObj) {
      if (parentObj.IsFiltered()) {
        if (fullDBObjSet.has(parentObj)) {
          if (parentObj.CheckIsInChainOfCommand(curSubchart)) return false;
          else if (parentObj != curSubchart.GetTopDBObj()) return true;
        }
      }
      parentObj = parentObj.GetParent();
    }
    return false;
  }

  //DB Overrides
  ApplyChanges(targetDBObj) {
    /*
  Apply all changes under an Override DB Object (override class)
  */
    if (!this.fChanges || this.fChanges.length === 0) return;

    var wsMgr = Globals._wsMgr;
    var theDoc = wsMgr.GetDoc();
    var newAdd = null;
    var parentClass = this.GetParentClass();
    var refDBObj = targetDBObj ?? parentClass.GetBaseRecord(this);

    // Refresh the db obj reference since it might be stale
    if (
      !theDoc.IsPlanChart() &&
      !refDBObj &&
      this.SmartGetFieldValue("Type") === enumOverrideType.add
    ) {
      const objType = this.SmartGetFieldValue("objType") ?? "Holder";
      const className = objTypeClassMap[objType];
      const objDb = parentClass.GetObjDB();
      const objClass = objDb.GetClassByName(className);
      const objMgr = objClass.GetObjMgr();
      refDBObj = objMgr.AddNewObject();
      newAdd = refDBObj;
    }

    // Reattach db obj refs if they get removed at runtime as a result of a chart rebuild (data sync)
    if (refDBObj != null && refDBObj.fCloneBase == null) {
      refDBObj.fCloneBase = refDBObj.Clone(true);
    }

    this.UpdateBaseRecordReference(refDBObj);

    this.ProcessChanges(refDBObj);

    if (theDoc.IsCustomChart()) {
      Globals._wsMgr.BroadcastTopic("CustomChartChangesLoaded");
    }

    return newAdd;
  }

  /**
   *
   * @param {QObjDBObject} baseRecord
   * @returns {void}
   */
  ApplyChangesReadOnly(baseRecord) {
    this.UpdateBaseRecordReference(baseRecord);

    const changeDeltaList = this.GetAggregatedChangeDelta(baseRecord);
    if (!changeDeltaList) {
      return;
    }
    // apply field changes to base record
    this.ProcessFieldChanges(baseRecord);
    // handle moves
    this.ProcessMoveChange(baseRecord, changeDeltaList, true);
    this.ProcessAssignmentChanges(baseRecord, changeDeltaList, true);
  }

  /**
   *
   * @param {QObjDBObject} baseRecord
   * @returns {void}
   */
  ProcessChanges(baseRecord) {
    const changeDeltaList = this.GetAggregatedChangeDelta(baseRecord);
    if (!changeDeltaList) {
      return;
    }

    var wsMgr = Globals._wsMgr;
    var docMgr = wsMgr.GetDocMgr();
    var theDoc = wsMgr.GetDoc();

    var objClass = baseRecord.GetParentClass();
    var parentRecord = baseRecord.GetParent();
    var type = this.SmartGetFieldValue("Type");
    var isPlanChart = docMgr.IsPlanChart();
    var addsPreProcessed = isPlanChart;

    // handle deletes
    if ([enumOverrideType.delete, enumOverrideType.moveout].includes(type)) {
      var objMgr = objClass.GetObjMgr();
      var revertDelete = this.IsReverted();
      const isPosition = baseRecord.IsPositionObj();
      if (isPlanChart) {
        // If changes were not reverted, check if this is a position and has been reopened
        const posStatusFName = objClass.GetFieldDefByID(kPositionStatus)?.GetFieldName?.();
        if (
          !revertDelete &&
          baseRecord.IsPositionObj() &&
          posStatusFName &&
          changeDeltaList[posStatusFName] === PositionStatus.Active
        ) {
          revertDelete = true;
        }
        baseRecord.SetDeleteFilter(revertDelete);
      } else {
        objMgr.ApplyDeleteFiltering(revertDelete, baseRecord, isPosition);
      }
    }

    // Handled reverted add
    if (type === enumOverrideType.add && !addsPreProcessed) {
      if (this.IsReverted()) {
        theDoc.DeleteBranch(baseRecord, parentRecord);
        return;
      }
    }

    // Apply assignment changes first
    this.ProcessAssignmentChanges(baseRecord, changeDeltaList);
    // apply field changes to base record
    this.ProcessFieldChanges(baseRecord);
    // handle moves
    this.ProcessMoveChange(baseRecord, changeDeltaList);
  }

  /**
   *
   * @param {QObjDBObject} baseRecord
   */
  ProcessFieldChanges(baseRecord) {
    var wsMgr = Globals._wsMgr;
    var docMgr = wsMgr.GetDocMgr();
    var objClass = baseRecord.GetParentClass();
    var isPlanChart = docMgr.IsPlanChart();
    var addsPreProcessed = isPlanChart;

    const changeDeltaList = this.GetAggregatedChangeDelta(baseRecord);
    if (!changeDeltaList) {
      return;
    }

    const recordIdFieldDef = objClass.GetRecordIDFieldDef();
    const recordIdFieldID = recordIdFieldDef.GetFieldID();
    const parentIdFieldDef = objClass.GetSupervisorIDFieldDef();
    const parentIdFieldID = parentIdFieldDef?.GetFieldID();
    for (var aChangeFieldName in changeDeltaList) {
      var fieldDef = objClass.GetFieldDefByName(aChangeFieldName);
      if (fieldDef == null) continue;

      var theChangeValue = changeDeltaList[aChangeFieldName];
      if (theChangeValue == null) continue;

      var changeFieldID = fieldDef.GetFieldID();

      // position charts aren't allowed to edit recordId or parentId
      if ([recordIdFieldID, parentIdFieldID].includes(changeFieldID) && addsPreProcessed) continue;

      if (changeFieldID === "Orphan") {
        baseRecord.SetAsOrphan(theChangeValue);
        continue;
      }

      baseRecord.SetFieldValue(fieldDef, theChangeValue, true);
    }
  }

  /**
   *
   * @param {QObjDBObject} baseRecord
   * @param {object} changeDeltaList
   * @param readOnly
   * @returns {void}
   */
  ProcessMoveChange(baseRecord, changeDeltaList, readOnly = false) {
    const objClass = baseRecord.GetParentClass();
    const docRef = objClass?.GetDocRef();
    const supervisorFieldDef = objClass?.GetSupervisorIDFieldDef();
    if (!docRef || !supervisorFieldDef) {
      return;
    }

    const type = this.SmartGetFieldValue("Type");

    const supervisorFieldName = supervisorFieldDef.GetFieldName();
    const targetSupervisorID = changeDeltaList[supervisorFieldName];
    if (
      targetSupervisorID == null ||
      [enumOverrideType.move, enumOverrideType.add].includes(type) === false
    ) {
      return;
    }

    const docMgr = docRef.GetDocMgr();
    const allDataLoaded = !docMgr?.IsServerChart();

    baseRecord.SetFieldValue(supervisorFieldDef, targetSupervisorID, true);

    if (readOnly) {
      return;
    }

    const recordIDFieldDef = objClass.GetRecordIDFieldDef();
    const parentBoxDBObj = objClass.FindObject(recordIDFieldDef, targetSupervisorID, false);
    if (parentBoxDBObj != null) {
      const parentBoxRecordID = parentBoxDBObj.GetObjectRecordID();
      const boxRecordID = baseRecord.GetObjectRecordID();
      if (parentBoxRecordID !== boxRecordID) {
        docRef.MoveBranch(baseRecord, parentBoxDBObj);
      }
    } else if (!allDataLoaded) {
      const parent = baseRecord.GetParent();
      docRef.DeleteBranch(baseRecord, parent);
    } else if (baseRecord.IsOrphan()) {
      docRef.DisconnectRoot(baseRecord, -1);
    }
  }

  /**
   *
   * @param {QObjDBObject} baseRecord
   * @param {object} changeDeltaList
   * @param readOnly
   * @returns {void}
   */
  ProcessAssignmentChanges(baseRecord, changeDeltaList, readOnly = false) {
    const objClass = baseRecord.GetParentClass();
    const docRef = objClass?.GetDocRef();
    // "Employee PositionID" exists on non-position (ADP) charts too, so gate on type, not the field.
    if (!docRef?.IsPositionDocument() || !baseRecord.IsHolderObj()) {
      return;
    }

    const employeePositionIDDef = objClass.GetFieldDefByID("Employee PositionID");
    if (!employeePositionIDDef) {
      return;
    }

    const type = this.SmartGetFieldValue("Type");
    const isNewHolder = type === enumOverrideType.add;
    const employeePositionFieldName = employeePositionIDDef.GetFieldName();
    const targetEmployeePositionId = changeDeltaList[employeePositionFieldName];

    if (
      targetEmployeePositionId == null ||
      ![enumOverrideType.move, enumOverrideType.add].includes(type)
    ) {
      return;
    }

    const positionClass = docRef.GetPositionClass();
    // Backstop: clone/deserialize can set hierarchy type without the Position class.
    if (!positionClass) {
      return;
    }
    const positionIDFieldDef = positionClass.GetRecordIDFieldDef();
    const currentPositionId = baseRecord.GetFieldValue(employeePositionIDDef);
    const currentPositionTargetObj = positionClass.FindObject(
      positionIDFieldDef,
      currentPositionId,
      false
    );

    baseRecord.SetFieldValue(employeePositionIDDef, targetEmployeePositionId);

    if (readOnly) {
      return;
    }

    const targetPositionDataObj = positionClass.FindObject(
      positionIDFieldDef,
      targetEmployeePositionId,
      false
    );

    docRef.MoveHolder(baseRecord, currentPositionTargetObj, targetPositionDataObj, isNewHolder);
  }

  IsReverted() {
    var revertedChanges = this.fChanges.filter(change => change.IsReverted());
    return revertedChanges.length > 0;
  }

  SetDeleteFilter(revert = false) {
    if (this.fIsDeleted === !revert) return;
    this.fIsDeleted = !revert;
  }

  //Returns the last non-reverted internal delta. If it returns null, the entire override should not be displayed.
  lastActiveChange(returnIndex) {
    var changeHistory = this.fChanges;
    if (!this.fChanges) return null;

    for (var index = changeHistory.length - 1; index >= 0; index--) {
      var mostRecentChange = changeHistory[index];
      if (mostRecentChange.IsReverted() == false) {
        if (returnIndex) return { index, mostRecentChange };
        return mostRecentChange;
      }
    }

    if (returnIndex) {
      return {
        mostRecentChange: null
      };
    }

    return null;
  }

  // Remove all changes
  RemoveChanges() {
    var changeList = this.fChanges;
    for (var i = changeList.length; i > 0; i--) {
      var theChange = changeList[i - 1];
      if (theChange.IsReverted() !== true) {
        theChange.fIsReverted = true;
      }
    }
  }

  ClearInvalidChanges() {
    this.fChanges = this.fChanges.filter(change => {
      return !(change.fChangeType === "Change" && Object.keys(change.GetDelta()).length === 0);
    });
  }

  UpdateBaseRecordReference(baseRecord) {
    if (!baseRecord) return;

    var changeList = this.fChanges;
    if (baseRecord.fCloneBase == null) baseRecord.fCloneBase = baseRecord.Clone(true);

    changeList.forEach(obj => {
      obj.fDBObj = baseRecord;
    });
  }

  /**
   * @returns {(QChange | null)}
   */
  FetchAndAggregateChanges(baseRecord) {
    var changeList = this.fChanges;
    if (!changeList || changeList.length === 0) return null;

    var flatDelta = this.FlattenChanges(baseRecord);
    const type = this.SmartGetFieldValue("Type");

    var { mostRecentChange } = this.lastActiveChange(true);
    if (mostRecentChange == null) {
      if (type !== enumOverrideType.change) return null;
      mostRecentChange = changeList[changeList.length - 1];
    }

    var newChange = mostRecentChange.Clone(this);
    newChange.fDelta = flatDelta;
    newChange.fChangeType = type;

    return newChange;
  }

  /**
   * Helper method to get aggregated change delta list
   * @param {QObjDBObject} baseRecord
   * @returns {(Object | null)} The delta list or null if not available
   */
  GetAggregatedChangeDelta(baseRecord) {
    if (!baseRecord) {
      return null;
    }

    const changeAggregate = this.FetchAndAggregateChanges(baseRecord);
    if (!changeAggregate) {
      return null;
    }

    const changeDeltaList = changeAggregate.GetDelta();
    return changeDeltaList;
  }

  FlattenChanges(baseRecord) {
    return this.fChanges.reduce((result, currentObj) => {
      var delta = currentObj.fDelta;
      var dbObj = currentObj.fDBObj ?? baseRecord;
      var objClass = dbObj.GetParentClass();
      for (const aChangeFieldName in delta) {
        var fieldDef = objClass.GetFieldDefByName(aChangeFieldName);
        if (fieldDef == null) continue;
        var theChangeValue = "";
        if (currentObj.IsConflicted() || currentObj.IsReverted() || !currentObj.IsDateEffective()) {
          theChangeValue = currentObj.fDBObj.fCloneBase?.GetFieldValue(fieldDef, false);
        } else {
          theChangeValue = delta[aChangeFieldName];
        }
        if (theChangeValue == null) continue;
        result[aChangeFieldName] = theChangeValue;
      }
      return result;
    }, {});
  }

  InitObject(parentClass) {
    this.fParentClass = parentClass;
    this.fFieldValues = [];
  }

  GetXMLElementName() {
    return "qo";
  }

  InitNewObject(parentClass) {
    this.InitObject(parentClass);
    var className = parentClass.GetClassName();

    var objectID = parentClass.GenUID();
    var idDef = this.GetFieldDefByID("ID"); // this.GetIndexFieldDef(0);
    this.AddNewFieldValue(idDef, objectID);
    if (className == enumObjClassNames.box) {
      //Normal chart box class
      var fDef = this.GetFieldDefByID("Orphan");
      this.AddNewFieldValue(fDef, 0);

      fDef = this.GetFieldDefByID("TopOfSubChart");
      this.AddNewFieldValue(fDef, 0);

      fDef = this.GetFieldDefByID("Locked");
      this.AddNewFieldValue(fDef, 0);
      fDef.SetManualAdd(true); // Needs to carry over on data refresh

      fDef = this.GetFieldDefByID("Persist");
      this.AddNewFieldValue(fDef, 0);
      fDef.SetManualAdd(true); // Needs to carry over on data refresh
    } else if (className == "Override") {
    } else if (className == "Position") {
      var fDef = this.GetFieldDefByID("Orphan");
      this.AddNewFieldValue(fDef, 0);

      fDef = this.GetFieldDefByID("TopOfSubChart");
      this.AddNewFieldValue(fDef, 0);

      fDef = this.GetFieldDefByID("Locked");
      this.AddNewFieldValue(fDef, 0);
      fDef.SetManualAdd(true); // Needs to carry over on data refresh

      fDef = this.GetFieldDefByID("Persist");
      this.AddNewFieldValue(fDef, 0);
      fDef.SetManualAdd(true); // Needs to carry over on data refresh

      this.SetHolderList([]);
    }
  }

  IsPersistent() {
    return this.fPersist;
  }

  IsFiltered() {
    if (this.IsMuted()) {
      // || this.IncludedFilteredManager())
      return false; // fugly
    }

    if (this.fFiltered) {
      return true;
    }

    if (!this.fIsDeleted) {
      return false;
    }

    if (Globals._publishRef) {
      return !Globals._pubSettings.fShowOverlay;
    }

    if (isInPlanning()) {
      return !Globals._uiConfig.fShowOverlay;
    }

    return false;
  }

  IsDeleted() {
    return this.fIsDeleted;
  }

  IsMuted() {
    return this.fMuted;
  }

  SetMuted(isMuted) {
    this.fMuted = isMuted;
  }

  Journal(newObject) {
    this.fParentClass.Journal(this, newObject);
  }

  SetPersistent(persistent) {
    this.Journal(false);
    this.fParentClass.UpdateNonPersistList(this, persistent);
    this.fPersist = persistent;
  }

  // Clone persisted/domain data only; do not copy transient UI/runtime state (for example fFiltered/fMuted).
  // Those flags will be re-evaluated based on the current user and chart context.
  Clone(ignoreInsert, newClass, copyNullish) {
    var targetClass = this.fParentClass;
    if (newClass != null) targetClass = newClass;

    if (ignoreInsert) {
      var newObj = new QObjDBObject();
      newObj.InitObject(targetClass);
    } else var newObj = targetClass.AddNewObject();

    var n = targetClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fDef = targetClass.GetIndexFieldDef(i);
      //	var fDefName = fDef.GetFieldName();
      //	var sysGen = (fDefName== "ID") || (fDefName== "Orphan") || (fDefName == "TopOfSubChart");

      var fDefID = fDef.GetFieldID();
      var sysGen = SysGen(fDefID);
      //	var sysGen = (fDefID== "ID") || (fDefID== "Orphan") || (fDefID == "TopOfSubChart") || (fDefID == "Locked") || (fDefID == "Persist");

      var isCalc = fDef.GetCalcCode() != null && fDef.GetCalcCode() != -1;

      var cloneIt = !(sysGen || isCalc);
      if (cloneIt) {
        var fVal = this.GetFieldValue(fDef);
        if (fVal != null || copyNullish === true) newObj.SetFieldValue(fDef, fVal);
      }
    }

    if (this.fChanges) {
      var changeList = [];
      for (var i = 0; i < this.fChanges.length; i++) {
        var theChange = this.fChanges[i];
        var newChangeObj = theChange.Clone(this);
        changeList.push(newChangeObj);
      }
      newObj.fChanges = changeList;
    }

    if (this.fHolderList) {
      newObj.SetHolderList(this.fHolderList.map(holder => holder.Clone(true)));
    }

    return newObj;
  }

  /**
   * @param {(QObjDBObject | null)} parentPosition
   * @returns {}
   */
  SetParentPosition(parentPosition) {
    this.fParentPosition = parentPosition;
  }

  GetParentPosition() {
    return this.fParentPosition;
  }

  /**
   * @param {(QObjDBObject[] | null)} holderList
   * @returns {}
   */
  SetHolderList(holderList) {
    this.Journal(false);
    for (const holder of holderList) {
      holder.SetParentPosition(this);
    }
    this.fHolderList = holderList;
  }

  GetHolderList() {
    return this.fHolderList;
  }

  NumHolders() {
    // Check if server provided a pre-calculated value for position charts
    var fieldDef = this.GetFieldDefByID("Holder Count");
    if (fieldDef && this.fFieldValues) {
      if (this.fFieldValIndex == null) this.BuildFieldValueIndex();
      var fV = this.fFieldValIndex?.FindObject(fieldDef.GetFieldUID());
      if (fV) {
        var serverValue = fV.GetValue();
        if (serverValue !== null && serverValue !== undefined && serverValue !== "") {
          return serverValue;
        }
      }
    }

    // Fallback to counting the in-memory holder list
    return this.fHolderList ? this.fHolderList.length : 0;
  }

  TransferForeign(foreignObj) {
    var n = this.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fDef = this.GetIndexFieldDef(i);
      var fDefID = fDef.GetFieldID();
      var sysGen = fDefID == "ID" || fDefID == "Orphan" || fDefID == "TopOfSubChart"; //|| (fDefID == "Locked") || (fDefID == "Persist")
      var isCalc = fDef.GetCalcCode() != null;
      var cloneIt = !(sysGen || isCalc);
      if (cloneIt) {
        var foreignFieldDef = foreignObj.GetFieldDefByID(fDefID);
        if (foreignFieldDef) {
          var fieldValue = foreignObj.GetFieldValue(foreignFieldDef);
          if (fieldValue != null) this.SetFieldValue(fDef, fieldValue);
        }
      }
    }
  }

  SerialClone(captureHierarchy) {
    var c = new QSerialObjDBObject();
    c.Init(this, captureHierarchy);

    return c;
  }

  GetBoxClass() {
    const parentClass = this.fParentClass;
    const objDB = parentClass.GetObjDB();
    return objDB.GetClassByName("Box");
  }

  InferObjectType() {
    var parentClass = this.GetParentClass();
    var className = parentClass.GetClassName();
    if (className === enumObjClassNames.position) {
      return enumObjectTypes.position;
    }

    return enumObjectTypes.holder;
  }

  InferObjectClassName() {
    return this.GetParentClass().GetClassName();
  }

  IsPositionObj() {
    return this.InferObjectType() === enumObjectTypes.position;
  }

  IsHolderObj() {
    return this.InferObjectType() === enumObjectTypes.holder;
  }

  /**
   * TEMP INS-7: agent detection via the "isAgent" dataset field until agent
   * objType tagging lands. Once enumObjectTypes.agent exists, replace the body
   * with `return this.InferObjectType() === enumObjectTypes.agent;`.
   * Dataset cells are strings, so only explicit affirmative markers count —
   * a whitelist, not a blacklist: any unrecognized value ("N", "F", "None",
   * typos, …) must classify as a regular record, because misclassifying an
   * employee replaces their entire profile card with the agent card. A
   * missing field def yields "" (falsy), keeping charts without the field
   * unaffected.
   * @returns {boolean}
   */
  IsAgentObj() {
    const value = this.SmartGetFieldValue("isAgent");
    if (!value) {
      return false;
    }
    return kAgentMarkers.includes(String(value).trim().toLowerCase());
  }

  /**
   * @returns {(QObjDBClass | null)}
   */
  GetParentClass() {
    return this.fParentClass;
  }

  /**
   * Will throw an error if no Parent Class present
   * @returns {string}
   */
  GetParentClassName() {
    return this.fParentClass.GetClassName();
  }

  GetObjectID() {
    if (this.fObjectID != null) return this.fObjectID;
    var idDef = this.GetFieldDefByID("ID");
    var theID = this.GetFieldValue(idDef);
    this.fObjectID = theID;
    return theID;
  }

  GetRecordIDFieldDef() {
    const parentClass = this.GetParentClass();
    return parentClass.GetRecordIDFieldDef();
  }

  GetTitleFieldDef() {
    const parentClass = this.GetParentClass();
    return parentClass.GetTitleFieldDef();
  }

  GetObjectRecordID() {
    var idDef = this.GetRecordIDFieldDef();
    var theID = null;
    if (idDef) theID = this.GetFieldValue(idDef);

    return theID;
  }

  GetObjectTitle() {
    var idDef = this.GetTitleFieldDef();
    var theID = null;
    if (idDef) theID = this.GetFieldValue(idDef);

    return theID;
  }

  GetRecordParentIDFieldDef() {
    const parentClass = this.GetParentClass();
    return parentClass.GetSupervisorIDFieldDef();
  }

  GetRecordIDFieldDef(fallbackName) {
    const parentClass = this.GetParentClass();
    return parentClass.GetRecordIDFieldDef(fallbackName);
  }

  GetObjectSupervisorID() {
    var idDef = this.GetRecordParentIDFieldDef();
    var superID = null;
    if (idDef) superID = this.GetFieldValue(idDef);

    return superID;
  }

  GetFieldCount() {
    return this.fParentClass.GetFieldCount();
  }

  GetIndexFieldDef(theIndex) {
    return this.fParentClass.GetIndexFieldDef(theIndex);
  }

  GetFieldDefByName(fieldName) {
    return this.fParentClass.GetFieldDefByName(fieldName);
  }

  GetFieldDefByLabel(fieldLabel) {
    return this.fParentClass.GetFieldDefByLabel(fieldLabel);
  }

  GetFieldDefByID(fieldID) {
    return this.fParentClass.GetFieldDefByID(fieldID);
  }

  ResetFieldIndex() {
    this.fFieldValIndex = null;
    this.fObjectID = null;

    // The fieldDef has been deleted...
    var newList = [];
    var n = this.fFieldValues.length;
    var pc = this.fParentClass;
    for (var i = 0; i < n; i++) {
      var fV = this.fFieldValues[i];
      var fieldDef = fV.GetFieldDef();
      if (pc.GetFieldDefIndex(fieldDef) != -1) newList.push(fV);
    }

    this.fFieldValues = newList;
  }

  // For load or import we don't need to lookup because we are adding a new value...
  AddNewFieldValue(fieldDef, fieldValue) {
    // New Value
    var newFV = new QObjDBFieldValue();
    newFV.InitFieldValue(fieldDef);
    newFV.SetValue(fieldValue);
    this.fFieldValues.push(newFV);
    this.fFieldValIndex = null;
    this.fObjectID = null;
  }

  SetSubordinateFieldValues(fieldDef, fieldValue) {
    var n = this.GetNumChildren();
    for (var i = 0; i < n; i++) {
      var aChild = this.GetIndexChild(i);
      aChild.SetFieldValue(fieldDef, fieldValue);
      aChild.SetSubordinateFieldValues(fieldDef, fieldValue);
    }
  }

  SetFieldValue(fieldDef, fieldValue) {
    this.Journal(false);
    if (this.fFieldValIndex == null) this.BuildFieldValueIndex();

    this.fObjectID = null;

    var fV = this.fFieldValIndex.FindObject(fieldDef.GetFieldUID());
    if (fV) {
      fV.SetValue(fieldValue);
      return;
    }

    // New Value
    this.AddNewFieldValue(fieldDef, fieldValue);
  }

  SmartSetFieldValue(fieldID, fieldValue) {
    var fieldDef = this.GetFieldDefByID(fieldID);
    if (!fieldDef) return;
    this.SetFieldValue(fieldDef, fieldValue);
  }

  //=================================================================================================
  //Calculations
  DoLevelCalc() {
    var level = 0;
    var curNode = this;

    while (curNode != null) {
      curNode = curNode.GetParent();
      level++;
    }

    return level;
  }

  DoAllocationRatio() {
    const parentClass = this.GetParentClass();
    const dividend = this.GetHolderList()?.length || 0;
    const divisorDef = parentClass.GetFieldDefByID("Desired Holders");
    const divisor = this.GetFieldValue(divisorDef) || 1;
    return dividend + "/" + divisor;
  }

  DoSOCCalc() {
    if (this.fParentPosition) {
      // On a position chart, count holders in direct child positions
      const n = this.fParentPosition.GetNumChildren();
      let count = 0;
      for (let i = 0; i < n; i++) {
        count += this.fParentPosition.GetIndexChild(i).GetHolderList()?.length ?? 0;
      }
      return count;
    }
    return this.GetNumChildren();
  }

  CountSubsLimit(limit) {
    var n = this.GetNumChildren();
    var subC = n;
    for (var i = 0; i < n; i++) {
      var aChild = this.GetIndexChild(i);
      subC += aChild.CountSubs() || 0;
      if (subC >= limit) return subC;
    }

    return subC;
  }

  //=====================================================================================
  // User Defined Formulas
  DoCustomCalc(fieldDef) {
    var r = fieldDef.GetFormulaRule();
    var calcType = r.GetFormulaType();

    switch (calcType) {
      case "countif":
      case "sumif":
      case "averageif":
        return this.DoCustomSum(fieldDef, calcType);
      case "concatenate":
        return this.DoConcatenate(fieldDef);
      case "lookup":
        return this.DoLookup(fieldDef);
      case "leftcalc":
        return this.DoSlice(fieldDef, true);
      case "rightcalc":
        return this.DoSlice(fieldDef, false);
      case "sum":
      case "average":
        return this.DoSum(fieldDef, calcType);
      case "map":
        return this.DoMap(fieldDef);
      case "math":
        return this.DoMath(fieldDef);
      case "min":
        return this.DoMaxMin(fieldDef, false);
      case "max":
        return this.DoMaxMin(fieldDef, true);
    }
  }

  DoMap(fieldDef) {
    var r = fieldDef.GetFormulaRule();
    var result = r.ProcessMap(this);
    return result;
  }

  DoConcatenate(fieldDef) {
    var r = fieldDef.GetFormulaRule();
    var p = this.GetParentClass();
    var docRef = p.GetDocRef();
    var result = r.ProcessConcat(this, fieldDef, docRef, false, false);
    return result;
  }

  FormatEmployeeLabel(fieldIdList, returnParts) {
    var isPositionObj = this.IsPositionObj();
    if (!fieldIdList)
      fieldIdList = isPositionObj
        ? Globals._uiConfig.fPositionLabelFields
        : Globals._uiConfig.fEmployeelabelFields;
    var formattedString = "";
    var n = fieldIdList?.length;
    var primaryString = null;
    for (var index = 0; index < n; index++) {
      var theField = fieldIdList[index];
      if (isPositionObj && theField === kPersonID) theField = kPositionID;
      if (isPositionObj && theField === kTitleID) theField = kPositionTitleID;
      var fieldValue = this.SmartGetFieldValue(theField);
      if (index === 0) primaryString = fieldValue;
      formattedString = FormatEmployeeLabelValue(
        formattedString,
        fieldValue,
        index,
        index === n - 1
      );
    }
    if (returnParts) return { primary: primaryString, full: formattedString };
    return formattedString;
  }

  DoRMaxMin(fieldDef, rule, curVal, doMax) {
    if (!curVal.fSkipFirst) {
      var v1 = rule.GetSumVal(this);
      if (isBlockedField(v1) || isBlockedField(curVal.fValue)) curVal.fValue = kBlockedFieldTag;
      else if (doMax) curVal.fValue = Math.max(curVal.fValue, v1);
      else curVal.fValue = Math.min(curVal.fValue, v1);
    } else curVal.fSkipFirst = false;

    var n = this.GetNumChildren();
    for (var i = 0; i < n; i++) {
      var aChild = this.GetIndexChild(i);
      aChild.DoRMaxMin(fieldDef, rule, curVal, doMax);
    }
  }

  DoMaxMin(fieldDef, doMax) {
    var r = fieldDef.GetFormulaRule();
    var s = r.GetScope();
    var startVal = new minMaxValue(-Number.MAX_VALUE);
    if (!doMax) startVal.fValue = -startVal.fValue;

    switch (s) {
      case enumFormulaScope.selfsubs:
        this.DoRMaxMin(fieldDef, r, startVal, doMax);
        break;
      case enumFormulaScope.subs:
        startVal.fSkipFirst = true;
        this.DoRMaxMin(fieldDef, r, startVal, doMax);
        break;
      case enumFormulaScope.directreports:
      case enumFormulaScope.selfdirreports:
        if (s == enumFormulaScope.selfdirreports) {
          startVal.fValue = r.GetSumVal(this);
          if (isBlockedField(startVal.fValue)) return kBlockedFieldTag;
        }
        var n = this.GetNumChildren();
        for (var i = 0; i < n; i++) {
          var aChild = this.GetIndexChild(i);
          var v1 = r.GetSumVal(aChild);
          if (isBlockedField(v1)) return kBlockedFieldTag;
          if (doMax) startVal.fValue = Math.max(startVal.fValue, v1);
          else startVal.fValue = Math.min(startVal.fValue, v1);
        }
        break;
    }

    return startVal.fValue;
  }

  DoMath(fieldDef) {
    var r = fieldDef.GetFormulaRule();
    var p = this.GetParentClass();
    var docRef = p.GetDocRef();
    var result = r.ProcessMath(this, fieldDef, docRef);
    return result;
  }

  DoSlice(fieldDef, sliceLeft) {
    var r = fieldDef.GetFormulaRule();
    var result = r.ProcessSlice(this, fieldDef, sliceLeft);
    return result;
  }

  DoLookup(fieldDef) {
    var r = fieldDef.GetFormulaRule();
    var f1 = r.GetFieldID1();
    var f2 = r.GetFieldID2();
    var lookupDef = this.GetFieldDefByID(f1);
    if (lookupDef == null) return "";

    var lookupValue = this.GetFieldValue(lookupDef);
    if (lookupValue == null || lookupValue == "") return "";

    var personDef = this.GetRecordIDFieldDef(true);
    var lookupObj = this.fParentClass.FindObject(personDef, lookupValue, true);

    var lookUpVal = "";
    if (lookupObj != null) {
      var valueDef = this.GetFieldDefByID(f2);
      if (valueDef) lookUpVal = lookupObj.GetFieldValue(valueDef);
    }

    return lookUpVal;
  }

  DoCustomSumSubs(parentObj, r, levels) {
    if (levels < 1) {
      return;
    }
    var calcType = r.GetFormulaType();

    var n = parentObj.GetNumChildren();
    for (var i = 0; i < n; i++) {
      var aChild = parentObj.GetIndexChild(i);
      var docRef = parentObj.fParentClass.GetDocRef();
      if (r.MatchRule(aChild, docRef) && !aChild.IsAux()) {
        this.count += 1;
        if (calcType === "sumif" || calcType === "averageif") {
          var contrib = r.GetSumVal(aChild);
          if (isBlockedField(contrib)) this.sum = kBlockedFieldTag;
          else if (!isBlockedField(this.sum)) this.sum += contrib;
        }
      }
      this.DoCustomSumSubs(aChild, r, levels - 1);
    }
  }

  IsAux() {
    var isAux = null;
    var fieldDef = this.GetFieldDefByID("isAux");
    if (fieldDef) isAux = this.GetFieldValue(fieldDef);
    //leaving the option for string flag here for legacy WB configs
    return isAux >= 1 || isAux == "1";
  }

  DoCustomSum(fieldDef, calcType) {
    var r = fieldDef.GetFormulaRule();
    this.count = 0;
    this.sum = 0;

    var s = r.GetScope();
    var sumBranch = false;
    var sumDirectSubs = false;
    var sumSelf = false;
    switch (s) {
      case enumFormulaScope.selfsubs:
        sumSelf = true;
        sumBranch = true;
        break;
      case enumFormulaScope.subs:
        sumBranch = true;
        break;
      case enumFormulaScope.directreports:
        sumDirectSubs = true;
        break;
      case enumFormulaScope.selfdirreports:
        sumDirectSubs = true;
        sumSelf = true;
        break;
    }
    if (sumSelf) {
      var docRef = this.fParentClass.GetDocRef();
      if (r.MatchRule(this, docRef) && !this.IsAux()) {
        this.count += 1;
        if (calcType === "sumif" || calcType === "averageif") {
          var selfContrib = r.GetSumVal(this);
          if (isBlockedField(selfContrib)) this.sum = kBlockedFieldTag;
          else if (!isBlockedField(this.sum)) this.sum += selfContrib;
        }
      }
    }
    if (sumDirectSubs) {
      this.DoCustomSumSubs(this, r, 1); // DBOBJ, Rule, Levels
    } else if (sumBranch) {
      this.DoCustomSumSubs(this, r, 100);
    }

    if (calcType === "countif") {
      return this.count;
    } else if (calcType === "sumif") {
      return this.sum;
    } else if (calcType === "averageif") {
      if (isBlockedField(this.sum)) return kBlockedFieldTag;
      var v = this.sum / this.count;
      v = parseFloat(v);
      if (isNaN(v)) {
        v = 0.0;
      }
      return v;
    }
    this.delete(this.sum);
    this.delete(this.count);
    return 0;
  }

  DoSumSubs(rule, valCount) {
    var v = rule.GetSumVal(this);
    if (isBlockedField(v)) return kBlockedFieldTag;
    var n = this.GetNumChildren();
    for (var i = 0; i < n; i++) {
      valCount.fCount += 1;
      var aChild = this.GetIndexChild(i);
      var childV = aChild.DoSumSubs(rule, valCount);
      if (isBlockedField(childV)) return kBlockedFieldTag;
      v += childV;
    }

    return v;
  }

  // Self-excluded sum for enumFormulaScope.subs; avoids the earlier
  // compute-then-subtract pattern that falsely blanked on self-blocked.
  DoSumDescendants(rule, valCount) {
    var v = 0;
    var n = this.GetNumChildren();
    for (var i = 0; i < n; i++) {
      valCount.fCount += 1;
      var aChild = this.GetIndexChild(i);
      var childV = aChild.DoSumSubs(rule, valCount);
      if (isBlockedField(childV)) return kBlockedFieldTag;
      v += childV;
    }
    return v;
  }

  DoSum(fieldDef, calcType) {
    var r = fieldDef.GetFormulaRule();

    var s = r.GetScope();
    var v = 0;
    var sumDirectSubs = false;
    var valCount = new valueCount();

    switch (s) {
      case enumFormulaScope.selfsubs:
        v = this.DoSumSubs(r, valCount);
        if (isBlockedField(v)) return kBlockedFieldTag;
        break;
      case enumFormulaScope.subs:
        valCount.fCount = 0;
        v = this.DoSumDescendants(r, valCount);
        if (isBlockedField(v)) return kBlockedFieldTag;
        break;
      case enumFormulaScope.directreports:
        valCount.fCount = 0;
        sumDirectSubs = true;
        break;
      case enumFormulaScope.selfdirreports:
        v = r.GetSumVal(this);
        if (isBlockedField(v)) return kBlockedFieldTag;
        sumDirectSubs = true;
        break;
    }

    if (sumDirectSubs) {
      var n = this.GetNumChildren();
      for (var i = 0; i < n; i++) {
        var aChild = this.GetIndexChild(i);
        var childContrib = r.GetSumVal(aChild);
        if (isBlockedField(childContrib)) return kBlockedFieldTag;
        v += childContrib;
        valCount.fCount += 1;
      }
    }

    if (calcType == "average") v = v / valCount.fCount;

    return v;
  }

  CountSubsInSubChart(isBottom, directsOnly) {
    if (isBottom) return 1;

    var n = this.GetNumChildren();
    var subC = n; //n;
    if (directsOnly) return subC;

    for (var i = 0; i < n; i++) {
      var aChild = this.GetIndexChild(i);
      if (aChild.TopOfSubChart() != 1) subC += aChild.CountSubsInSubChart();
    }

    return subC;
  }

  CountSubs() {
    if (this.fParentPosition) {
      // On a position chart, box hierarchy is not wired; compute subordinate holders
      // via the position hierarchy (child positions and their holders, recursively)
      return this._countHoldersInPositionChildren(this.fParentPosition);
    }
    var n = this.GetNumChildren();
    var subC = n;
    for (var i = 0; i < n; i++) {
      subC += this.GetIndexChild(i).CountSubs();
    }
    return subC;
  }

  _countHoldersInPositionChildren(position) {
    const n = position.GetNumChildren();
    let count = 0;
    for (let i = 0; i < n; i++) {
      const childPos = position.GetIndexChild(i);
      count += childPos.GetHolderList()?.length ?? 0;
      count += this._countHoldersInPositionChildren(childPos);
    }
    return count;
  }

  Headcount() {
    return this.CountSubs() + 1;
  }

  Peers() {
    if (this.fParentPosition) {
      // On a position chart, peers = all holders in sibling positions (children of
      // the parent position, including this position's own holders)
      const parentPos = this.fParentPosition.GetParent();
      if (!parentPos) return 0;
      let count = 0;
      const n = parentPos.GetNumChildren();
      for (let i = 0; i < n; i++) {
        count += parentPos.GetIndexChild(i).GetHolderList()?.length ?? 0;
      }
      return count;
    }
    return this.GetParent() ? this.GetParent().GetNumChildren() : 0;
  }

  PositionCount() {
    if (!this.IsPositionObj?.()) return 0;
    return this.CountPositionSubs() + 1;
  }

  SubordinatePositions() {
    if (!this.IsPositionObj?.()) return 0;
    return this.CountPositionSubs();
  }

  DirectSubordinatePositions() {
    if (!this.IsPositionObj?.()) return 0;
    var n = this.GetNumChildren(),
      count = 0;
    for (var i = 0; i < n; i++) {
      if (this.GetIndexChild(i).IsPositionObj?.()) count++;
    }
    return count;
  }

  PeerPositions() {
    if (!this.IsPositionObj?.()) return 0;
    var parent = this.GetParent();
    return parent && parent.IsPositionObj?.() ? parent.GetNumChildren() : 0;
  }

  // Helper method for fallback calculations only
  CountPositionSubs() {
    var n = this.GetNumChildren();
    var subC = 0;
    for (var i = 0; i < n; i++) {
      var aChild = this.GetIndexChild(i);
      if (aChild.IsPositionObj && aChild.IsPositionObj()) {
        subC += 1;
        subC += aChild.CountPositionSubs();
      }
    }
    return subC;
  }

  SupervisorID() {
    var pid = -1;
    var p = this.GetParent();
    if (p != null) pid = p.GetObjectID();
    return pid;
  }

  ReportsToName() {
    var mgrName = "";
    var p = this.GetParent();
    if (p != null) {
      var namefieldDef = this.GetFieldDefByID("Name");
      if (namefieldDef) mgrName = p.GetFieldValue(namefieldDef);
    }

    return mgrName;
  }

  NewSupervisorID() {
    var newSuperID = "";
    var p = this.GetParent();
    if (p != null) {
      var personfieldDef = this.GetRecordIDFieldDef(false);
      if (personfieldDef) newSuperID = p.GetFieldValue(personfieldDef);
      else newSuperID = p.GetObjectID();
    }

    return newSuperID;
  }

  DoSubChartLevelCalc() {
    var curNode = this;
    var subchartContext = this.fParentClass.GetSubChartContext();
    var level = 1;
    while (curNode.GetParent() && curNode !== subchartContext) {
      curNode = curNode.GetParent();
      level++;
    }

    return level;
  }

  DoCalc(calcCode, fieldDef, invalidateCache) {
    var r = fieldDef.GetFormulaRule();
    //if (!r)
    //	return appstr("refinvalid");
    var objID = this.GetObjectID();

    var v = r.CheckCache(objID);
    if (v == null || invalidateCache) {
      switch (calcCode) {
        case enumCalcCode.level:
          v = this.DoLevelCalc();
          break;
        case enumCalcCode.spanofcontrol:
          v = this.DoSOCCalc();
          break;
        case enumCalcCode.subordinates:
          v = this.CountSubs();
          break;
        case enumCalcCode.headcount:
          v = this.Headcount();
          break;
        case enumCalcCode.peers:
          v = this.Peers();
          break;
        case enumCalcCode.reportstoname:
          v = this.ReportsToName();
          break;
        case enumCalcCode.supervisorID:
        case enumCalcCode.newsupervisorID:
          v = this.NewSupervisorID();
          break;
        case enumCalcCode.subchartlevel:
          v = this.DoSubChartLevelCalc();
          break;
        case enumCalcCode.ninebox:
          v = this.DoNineBoxCalc();
          break;
        case enumCalcCode.customcalc:
          v = this.DoCustomCalc(fieldDef);
          break;
        case enumCalcCode.ratio:
          v = this.DoAllocationRatio();
          break;
        case enumCalcCode.holderCount:
          v = this.NumHolders();
          break;
        case enumCalcCode.positioncount:
          v = this.PositionCount();
          break;
        case enumCalcCode.subordinatepositions:
          v = this.SubordinatePositions();
          break;
        case enumCalcCode.directsubordinatepositions:
          v = this.DirectSubordinatePositions();
          break;
        case enumCalcCode.peerpositions:
          v = this.PeerPositions();
          break;
      }
      //refLib
      const failResults = [
        appstr("refinvalid"),
        appstr("refnotfound"),
        appstr("refnotconfig"),
        appstr("loading")
      ];
      var noCacheInputs = "[subchart]";
      if (
        r.CheckCache(objID) == null &&
        failResults.indexOf(v) === -1 &&
        r.fFormulaStr &&
        noCacheInputs.indexOf(r.fFormulaStr.toLowerCase()) === -1
      ) {
        //doing this to not interfere with the custom cache management systems here.
        r.AddToCache(objID, v);
      }
    }

    return v;
  }

  DoNineBoxCalc() {
    var perfCode = "0";
    var potCode = "0";

    var perfField = this.GetFieldDefByID("Performance");
    if (perfField == null) return NineBoxCode(perfCode, potCode);

    var potField = this.GetFieldDefByID("Potential");
    if (potField == null) return NineBoxCode(perfCode, potCode);

    var perf = this.GetFieldValue(perfField);
    var pot = this.GetFieldValue(potField);

    if (pot == null || perf == null) return NineBoxCode(perfCode, potCode);

    if (pot != null) pot = pot.toString();

    var potDetector = pot.toLowerCase();
    //TODO: Let's add the includes usage to QPolyFill so we don't need to deal with IE11 limitations here.
    if (
      potDetector.includes("high") ||
      potDetector.includes("exceeds") ||
      potDetector.toLowerCase() == "a"
    )
      pot = "1";
    else if (
      potDetector.includes("medium") ||
      potDetector.includes("meets") ||
      potDetector.includes("valued") ||
      potDetector.toLowerCase() == "b"
    )
      pot = "2";
    else if (potDetector.toLowerCase() == "c") pot = "3";

    switch (pot) {
      case "1":
      case "2":
      case "3":
        potCode = pot;
        break;
      default:
        potCode = "0";
        break;
    }

    if (perf != null) perf = perf.toString();

    var perfDetector = perf.toLowerCase();
    //TODO: Let's add the includes usage to QPolyFill so we don't need to deal with IE11 limitations here.
    if (
      perfDetector.includes("high") ||
      perfDetector.includes("exceeds") ||
      perfDetector.includes("leading")
    )
      perf = "1";
    else if (
      perfDetector.includes("medium") ||
      perfDetector.includes("meets") ||
      perfDetector.includes("performing") ||
      perfDetector.includes("developing") ||
      perfDetector.includes("new") ||
      perfDetector.includes("not rated")
    )
      perf = "2";

    switch (perf) {
      case "1":
        perfCode = "A";
        break;
      case "2":
        perfCode = "B";
        break;
      case "3":
        perfCode = "C";
        break;
      case "A":
      case "a":
      case "B":
      case "b":
      case "C":
      case "c":
        perfCode = perf.toUpperCase();
        break;
      default:
        //This is intentional! Force 00 case for missing pot/pot info
        potCode = "0";
        break;
    }

    if (potCode == "0") perfCode = "0";

    return NineBoxCode(perfCode, potCode);
  }

  GetAssistantFlag(aDef) {
    if (aDef == null) aDef = this.GetFieldDefByID("isAssistant");

    if (aDef != null) {
      var isAssist = this.GetFieldValue(aDef);
      if (isAssist > 0) return isAssist;
    }

    return 0;
  }

  TopOfSubChart() {
    var aDef = this.GetFieldDefByID("TopOfSubChart");
    var isTop = this.GetFieldValue(aDef);

    return isTop;
  }

  IsOrphan() {
    var aDef = this.GetFieldDefByID("Orphan");
    var isOrphan = this.GetFieldValue(aDef);

    if (isOrphan == 1) return true;

    //2020-05-26 - the below call is somehow causing a crash with coheads having references to each other. Because should
    //Now flag each object in a branch when it gets orphaned , this code should be unnecessary but the underlying crash is a problem.

    /*
    var parent = this.GetParent();
    if (parent != null)
	{
		console.log(parent.GetObjectPersonID());
        return parent.IsOrphan();
	}
    */
    return false;
  }

  GetLockStatus(fieldDef) {
    var aDef = fieldDef;

    if (aDef == null) aDef = this.GetFieldDefByID("Locked");

    var lockStatus = this.GetFieldValue(aDef);
    if (lockStatus == null) return enumLockStatus.notLocked;

    return lockStatus;
  }

  SetLockStatus(newLockStatus) {
    var aDef = this.GetFieldDefByID("Locked");
    // Is it possible that def doesn't exist?
    this.SetFieldValue(aDef, newLockStatus);
  }

  GetPersistStatus(fieldDef) {
    var aDef = fieldDef;

    if (aDef == null) aDef = this.GetFieldDefByID("Persist");

    var persistStatus = this.GetFieldValue(aDef);
    if (persistStatus == null) return enumPersistStatus.nopersist;

    return persistStatus;
  }

  SetPersistStatus(persistStatus) {
    var aDef = this.GetFieldDefByID("Persist");
    this.SetFieldValue(aDef, persistStatus);
  }

  BuildFieldValueIndex() {
    var valueIndex = new QGenericIndex();
    valueIndex.InitIndex();
    var n = this.fFieldValues.length;

    for (var i = 0; i < n; i++) {
      var fV = this.fFieldValues[i];
      var fieldDef = fV.GetFieldDef();
      valueIndex.AddToIndex(fV, fieldDef.GetFieldUID());
    }
    this.fFieldValIndex = valueIndex;
  }

  /**
   * @param {string} fieldId
   * @returns {unknown}
   */
  SmartGetFieldValue(defID) {
    var fieldDef = this.GetFieldDefByID(defID);
    if (fieldDef) {
      return this.GetFieldValue(fieldDef, false);
    }
    return "";
  }

  /**
   * Display-safe wrapper around GetFieldValue.
   * Returns the localized "(hidden)" string for blocked fields instead of the raw sentinel.
   * Use this at any call site that renders the value in the UI.
   *
   * @param {QObjDBFieldDef} fieldDef
   * @param {boolean=} invalidateCache
   * @returns {unknown}
   */
  GetDisplayFieldValue(fieldDef, invalidateCache) {
    return sanitizeBlockedValue(this.GetFieldValue(fieldDef, invalidateCache));
  }

  /**
   * Display-safe wrapper around SmartGetFieldValue.
   * Returns the localized "(hidden)" string for blocked fields instead of the raw sentinel.
   * Use this at any call site that renders the value in the UI.
   *
   * @param {string} defID
   * @returns {unknown}
   */
  SmartGetDisplayFieldValue(defID) {
    return sanitizeBlockedValue(this.SmartGetFieldValue(defID));
  }

  /**
   * @param {QObjDBFieldDef} fieldDef
   * @param {boolean=} invalidateCache
   * @param {boolean=} baseValue
   * @returns {unknown}
   */
  GetFieldValue(fieldDef, invalidateCache, baseValue) {
    var calcCode = fieldDef.GetCalcCode();
    if (calcCode == enumCalcCode.aggregatedField) return null;

    if (fieldDef.GetPlanningDef() != null && !baseValue) fieldDef = fieldDef.GetPlanningDef();
    if (calcCode != null && calcCode != enumCalcCode.static)
      return this.DoCalc(calcCode, fieldDef, invalidateCache);

    if (this.fFieldValIndex == null) this.BuildFieldValueIndex();

    var fV = this.fFieldValIndex.FindObject(fieldDef.GetFieldUID());
    if (fV) {
      // Verify UID-based match is the correct field. UIDs are per-class-instance
      // sequential IDs, so when a layout resolves field defs against a different
      // class instance than the one that indexed values, UIDs can collide with
      // the wrong field. Fall back to field-ID scan when there's a mismatch.
      var foundDef = fV.GetFieldDef();
      if (foundDef.GetFieldID() === fieldDef.GetFieldID()) {
        var val = fV.GetValue();
        return val;
      }
      // UID collision - fall through to linear scan.
      // Rare: happens on position charts with mixed-class layouts where a
      // layout resolves field defs from a different class instance than the
      // one that built this record's FieldValue index.
    }
    // Fallback: linear scan by field ID for cross-class-instance lookups
    var n = this.fFieldValues.length;
    for (var i = 0; i < n; i++) {
      var fieldValue = this.fFieldValues[i];
      var fDef = fieldValue.GetFieldDef();
      if (fDef.GetFieldID() === fieldDef.GetFieldID()) {
        return fieldValue.GetValue();
      }
    }
    return null;
  }

  GetRecordDetails(identifier) {
    return this.fRecordDetails[identifier];
  }

  SetRecordDetails(identifier, details, layout) {
    if (this.fRecordDetails == null) this.fRecordDetails = {};
    this.fRecordDetails[identifier] = layout.BuildValueMatrix(details);
  }

  GetAuxCleanID(personIDDef, AuxDef) {
    if (!AuxDef || this.GetFieldValue(AuxDef) == "0") {
      return this.GetFieldValue(personIDDef);
    }

    var uncleanID = this.GetFieldValue(personIDDef);
    let cleanID = uncleanID;
    if (uncleanID.indexOf("-aux") > -1) {
      const isAuxValue = this.SmartGetFieldValue("isAux");
      const parent = this.GetParent();
      const parentAuxValue = parent?.SmartGetFieldValue("isAux") ?? null;

      if (isAuxValue === parentAuxValue) {
        const parentRecordId = parent.GetFieldValue(personIDDef);

        cleanID = uncleanID.split(`-${parentRecordId}`)[0];
      } else {
        cleanID = uncleanID.split("-aux")[0];
      }
    }

    return cleanID;
  }

  FindAuxBaseRecord(planManager = null) {
    const idFieldDef = this.GetRecordIDFieldDef();
    const auxFieldDef = this.GetFieldDefByID("isAux");
    if (!auxFieldDef) {
      return this;
    }

    const thisRecordId = this.GetFieldValue(idFieldDef);
    const baseRecordId = this.GetAuxCleanID(idFieldDef, auxFieldDef);
    if (baseRecordId === thisRecordId) {
      return this;
    }

    const parentClass = this.GetParentClass();
    const baseDbObj = parentClass.FindObject(idFieldDef, baseRecordId, true);
    if (baseDbObj) {
      return baseDbObj;
    }

    if (!planManager) {
      return null;
    }

    const planMgrCacheHit = planManager.fCloneBaseCache[baseRecordId];
    if (planMgrCacheHit) {
      return planMgrCacheHit;
    }

    return null;
  }

  GetSubNameList() {
    var v = "";
    var namefieldDef = this.GetFieldDefByID("Name");
    if (namefieldDef) {
      var n = this.GetNumChildren();
      for (var i = 0; i < n; i++) {
        var child = this.GetIndexChild(i);
        var childName = child.GetFieldValue(namefieldDef);
        v += childName;
        if (i != n - 1) v += ",";
      }
    }

    return v;
  }

  GetChainOfCommand() {
    var namefieldDef = this.GetFieldDefByID("Name");
    if (namefieldDef == null) return "";

    var finalVal = SmartGetFieldValue(this, namefieldDef);
    var pObj = this.GetParent();
    while (pObj != null) {
      var parentName = SmartGetFieldValue(pObj, namefieldDef);
      finalVal += " > " + parentName;
      pObj = pObj.GetParent();
    }

    return finalVal;
  }

  MatchFieldValue(fieldDef, matchValue, startsWith, options) {
    const splitchar = options?.splitchar ?? null;
    const strictEmpty = options?.strictEmpty ?? false;

    var dataVal = this.GetFieldValue(fieldDef);
    if (dataVal == null) {
      return false;
    }

    // Replace __blocked__ sentinel with display value so search matches what the user sees
    if (isBlockedField(dataVal)) {
      dataVal = getBlockedFieldDisplayValue();
    }

    if (splitchar) {
      var dataVals = dataVal.split(splitchar);
    } else {
      var dataVals = [dataVal];
    }

    for (var valIdx in dataVals) {
      var candidateVal = dataVals[valIdx];
      if (candidateVal != null) {
        if (typeof candidateVal != "string") candidateVal = candidateVal.toString();
        if (startsWith) {
          if (Array.isArray(matchValue)) {
            return matchValue.some(function (matchStr) {
              return candidateVal.toLowerCase().indexOf(matchStr.toLowerCase()) == 0;
            });
          }

          if (candidateVal.toLowerCase().indexOf(matchValue.toLowerCase()) == 0) return true;
        } else {
          let regEx;
          if (Array.isArray(matchValue)) {
            regEx = CreateArrayRegExpression(matchValue, true, strictEmpty);
          } else {
            regEx = CreateRegExpression(matchValue, true, strictEmpty);
          }
          if (candidateVal.match(regEx) !== null) return true;
        }
      }
    }
    return false;
  }

  CompressObj(refTable) {
    var n = this.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = this.GetIndexFieldDef(i);
      var calcCode = fieldDef.GetCalcCode();
      if (calcCode == null) {
        var fieldID = fieldDef.GetFieldID();
        var sysGen = SysGen(fieldID) || ExcludeFromCompress(fieldID);
        if (!sysGen) {
          var fieldValue = this.GetFieldValue(fieldDef);
          if (fieldValue != null) refTable.AddRefValue(fieldID, fieldValue);
        }
      }
    }
  }

  LoadFromJSON(json, employeeMap) {
    this.SetTempChildIndex(-1);
    var plFound = false;
    var sCTFound = false;

    var parentClass = this.fParentClass;
    var objDB = parentClass.GetObjDB();
    var version = objDB.GetVersion();

    this.fIsDeleted = json["deleted"] == true;
    this.fFiltered = this.fIsDeleted && json["filtered"] == true;

    this.SetTempParentID(json["pID"]);
    this.SetTempChildIndex(json["cIdx"]);

    var fieldList = json["fields"];

    for (var i = 0; i < fieldList.length; i++) {
      var fieldLookupObj = fieldList[i];
      var saveIdx = fieldLookupObj[0];
      //var field
      var fDef = parentClass.GetFieldDefBySaveIndex(saveIdx);
      if (!fDef) continue;
      var fieldID = fDef.GetFieldID();
      var fieldValue = fieldLookupObj[1];

      switch (fieldID) {
        case "ID":
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          this.AddNewFieldValue(fieldDef, fieldValue);
          fieldDef.SetReadOnly(true);
          break;
        case "Locked":
        case "Persist":
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          this.AddNewFieldValue(fieldDef, fieldValue);
          break;
        case "Orphan":
        case "TopOfSubChart":
          if (fieldID == "Orphan") plFound = true;
          if (fieldID == "TopOfSubChart") sCTFound = true;
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          this.AddNewFieldValue(fieldDef, fieldValue);
          fieldDef.SetReadOnly(true);
          break;
        case "AlsoReportsTo":
        case "Also^Reports^To":
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          try {
            fieldValue = JSON.parse(fieldValue);
          } catch {
            // This field was incorrectly stored with initial JSON serialization
            // This fallback handling allows these charts with bad data to still load
            fieldValue = [];
          }
          this.AddNewFieldValue(fieldDef, fieldValue);
          fieldDef.SetReadOnly(true); // why here?
          break;
        default:
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          var isCompressed = fieldDef.GetCompressed();
          if (isCompressed && fieldDef["lookup"]) {
            var lookup = fieldDef["lookup"];
            var v = lookup.LookupRef(fieldValue);
            this.AddNewFieldValue(fieldDef, v);
          } else this.AddNewFieldValue(fieldDef, fieldValue);
          break;
      }
    }

    if (json["cloneBase"]) {
      var cloneBase = new QObjDBObject();
      cloneBase.InitObject(this.fParentClass);
      cloneBase.LoadFromJSON(json["cloneBase"]);
      this.fCloneBase = cloneBase;
    }

    if (json["recordDetails"]) {
      var details = json["recordDetails"];
      var detailsFD = parentClass.GetFieldDefByID("_objInfo");
      if (!detailsFD) {
        this.AddNewFieldValue(detailsFD, details);
      } else {
        this.SmartSetFieldValue("_objInfo", details);
      }
    }

    if (this.fParentClass.GetClassName() === enumObjClassNames.override) {
      var dbObj = this.fParentClass.GetBaseRecord(this);
      this.fChanges = [];
      if (json["changes"]) {
        var wsMgr = parentClass.fWSMGr;
        var changeList = json["changes"];

        for (var j = 0; j < changeList.length; j++) {
          var theChange = changeList[j];
          var newChange = new QChange();
          newChange.InitChange(wsMgr, theChange, this, dbObj, this.fParentClass);
          this.fChanges.push(newChange);
        }
      }
    }

    if (this.fParentClass.GetClassName() === enumObjClassNames.position) {
      this.fHolderList = [];
      if (json["holderList"]) {
        var holders = json["holderList"];
        for (var k = 0; k < holders.length; k++) {
          var holderID = holders[k];
          var holderObj;
          if (employeeMap) {
            holderObj = employeeMap.get(holderID);
          } else {
            var boxClass = objDB.GetClassByName("Box");
            holderObj = boxClass.FindObject(boxClass.GetRecordIDFieldDef(), holderID);
          }
          if (holderObj) this.fHolderList.push(holderObj);
        }
      }
    }
  }

  LoadParentPosition(positionMap) {
    // Load Parent positions for Holders. Used in case positions are loaded first.
    if (this.GetParentPosition()) return;

    const parentClass = this.fParentClass;
    if (parentClass.GetClassName() !== enumObjClassNames.box) return;

    const positionClass = parentClass.GetObjDB().GetClassByName(enumObjClassNames.position);
    const parentPositionId = this.SmartGetFieldValue(kEmployeePositionID);

    if (parentPositionId) {
      const position = positionMap
        ? positionMap.get(parentPositionId)
        : positionClass.FindObject(positionClass.GetRecordIDFieldDef(), parentPositionId, true);
      this.SetParentPosition(position);
      // Also register in position's holder list so count methods work after
      // a full document reload (e.g. data sync via CopyAndApplyOverrides).
      // Use direct assignment to avoid the SetParentPosition loop in SetHolderList.
      if (position) {
        const hl = position.fHolderList ?? [];
        if (!hl.includes(this)) hl.push(this);
        position.fHolderList = hl;
      }
    }
  }

  /**
   *
   * @param {string[]=} colNames
   * @returns {*}
   */
  JSON(colNames) {
    //basic serialization method
    var outObj = {};
    var parentClass = this.fParentClass;
    var n = parentClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = parentClass.GetIndexFieldDef(i);
      // Don't output calculation values

      var fieldValue = this.GetFieldValue(fieldDef);
      var fieldName = fieldDef.GetFieldName();
      if (restrictedInternalFields.includes(fieldName)) continue;
      if (colNames != null && !colNames.includes(fieldName)) continue;
      //check if the field should be outputted
      outObj[fieldName] = fieldValue;
    }
    return outObj;
  }

  OutputToJSON() {
    var outObj = {};
    var parent = this.GetParent();
    var parentID = -1;
    var childIndex = -1;
    if (parent) {
      parentID = parent.GetObjectID();
      childIndex = parent.IndexOfChildNoFilter(this);
    }

    if (this.fIsDeleted) {
      outObj["deleted"] = true;
      if (this.IsFiltered()) outObj["filtered"] = true;
    }

    if (this.fCloneBase) {
      outObj["cloneBase"] = this.fCloneBase.OutputToJSON();
    }

    if (this.SmartGetFieldValue("_objInfo") != null) {
      outObj["recordDetails"] = this.SmartGetFieldValue("_objInfo");
    }

    outObj["pID"] = parentID;
    outObj["cIdx"] = childIndex;
    // Version 2+ file

    var ignoredFieldDefs = [this.GetFieldDefByID("_objInfo")];
    if (this["ignoreSCTop"] == true) {
      ignoredFieldDefs.push(this.GetFieldDefByID("TopOfSubChart"));
    }

    var parentClass = this.fParentClass;
    var refLib = parentClass.GetRefLib();

    var n = parentClass.GetFieldCount();
    var fieldVals = [];
    for (var i = 0; i < n; i++) {
      var fieldDef = parentClass.GetIndexFieldDef(i);
      // Don't output calculation values
      var calcCode = fieldDef.GetCalcCode();
      if (calcCode == null || calcCode == enumCalcCode.static) {
        var fieldValue = this.GetFieldValue(fieldDef);
        if (fieldValue != null) {
          if (!ignoredFieldDefs.includes(fieldDef)) {
            var fieldID = fieldDef.GetFieldID();
            var fieldIndex = fieldDef.GetSaveIndex();
            var isCompressed = fieldDef.GetCompressed();
            var sFieldValue = fieldValue.toString();
            if (fieldDef.GetFieldType() === enumFieldType.object) {
              fieldValue = JSON.stringify(fieldValue);
            } else if (refLib && isCompressed && sFieldValue != "") {
              // ref table is nuked after Box class objects are sent to xml
              fieldValue = refLib.GetReference(fieldID, sFieldValue);
            }
            if (fieldValue instanceof QObjDBObject) {
              continue;
            }
            var fieldObj = [fieldIndex, fieldValue];

            //fieldObj[fieldIndex] = fieldValue
            fieldVals.push(fieldObj);
          }
        }
      }
    }
    outObj["fields"] = fieldVals;

    if (parentClass.GetClassName() === enumObjClassNames.override) {
      if (this.fChanges) {
        var jsonChangeList = [];
        for (var j = 0; j < this.fChanges.length; j++) {
          var theChange = this.fChanges[j];
          var serializedChange = theChange.Serialize();
          jsonChangeList.push(serializedChange);
        }
        outObj["changes"] = jsonChangeList;
      }
    }

    if (parentClass.GetClassName() === enumObjClassNames.position) {
      outObj["holderList"] = this.fHolderList.map(holder => holder.GetObjectRecordID());
    }

    return outObj;
  }

  OutputSubElements(stream) {
    //var className = this.GetParentClassName();
    //this.OutputSimpleElement("classname", className, stream);

    var parent = this.GetParent();
    var parentID = -1;
    var childIndex = -1;
    if (parent) {
      parentID = parent.GetObjectID();
      childIndex = parent.IndexOfChildNoFilter(this);
    }
    if (this.IsFiltered()) this.OutputSimpleElement("f", "1", stream);
    //	this.OutputSimpleElement("parentid", parentID, stream);
    //	this.OutputSimpleElement("childindex", childIndex, stream);

    // Version 2+ file
    this.OutputSimpleElementF("pi", parentID, stream); // Saves 6 * 30000
    this.OutputSimpleElementF("ci", childIndex, stream); // Saves 8 * 30000

    var topSCfieldDef = null;
    if (this["ignoreSCTop"] == true) topSCfieldDef = this.GetFieldDefByID("TopOfSubChart");

    var parentClass = this.fParentClass;
    if (parentClass.GetClassName() === "Position")
      parentClass = parentClass.fObjDB.GetClassByName("Box");
    var refLib = parentClass.GetRefLib();

    var n = parentClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = parentClass.GetIndexFieldDef(i);
      // Don't output calculation values
      var calcCode = fieldDef.GetCalcCode();
      if (calcCode == null || calcCode == enumCalcCode.static) {
        var fieldValue = this.GetFieldValue(fieldDef);
        if (fieldValue != null) {
          if (fieldDef != topSCfieldDef) {
            var fieldID = fieldDef.GetFieldID();
            var fieldIndex = fieldDef.GetSaveIndex();
            var isCompressed = fieldDef.GetCompressed();
            var fieldIndex2 = "e" + fieldIndex;
            var sFieldValue = fieldValue.toString();

            if (fieldDef.GetFieldType() === enumFieldType.object) {
              fieldValue = JSON.stringify(fieldValue);
              this.OutputSimpleElement(fieldIndex2, fieldValue, stream);
            } else if (refLib && isCompressed && sFieldValue != "") {
              // ref table is nuked after Box class objects are sent to xml
              fieldValue = refLib.GetReference(fieldID, sFieldValue);
              this.OutputSimpleElementF(fieldIndex2, fieldValue, stream);
            } else this.OutputSimpleElement(fieldIndex2, fieldValue, stream);
          }
        }
      }
    }
  }

  DumpToConsole(msg) {
    var parent = this.GetParent();
    var parentID = -1;
    var childIndex = -1;
    if (parent) {
      parentID = parent.GetObjectID();
      childIndex = parent.IndexOfChild(this);
    }

    console.log("QObjDBObject:", msg);
    console.log("parentid:", parentID);
    console.log("childindex:", childIndex);

    var n = this.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = this.GetIndexFieldDef(i);
      var fieldValue = this.GetFieldValue(fieldDef);
      if (fieldValue != null) {
        var fieldName = fieldDef.GetFieldName();
        console.log("fieldname:", fieldName, " fieldValue:", fieldValue);
      }
    }
  }

  LoadSubElements(objTree) {
    var n = objTree.GetNumChildren();
    this.SetTempChildIndex(-1);
    var plFound = false;
    var sCTFound = false;

    var parentClass = this.fParentClass;
    if (parentClass.GetClassName() === "Position")
      parentClass = parentClass.GetObjDB().GetClassByName("Box");
    var objDB = parentClass.GetObjDB();
    var version = objDB.GetVersion();

    for (var i = 0; i < n; i++) {
      var objField = objTree.GetIndexChild(i);
      var fieldID = objField.GetElementName();
      if (version > 1) {
        switch (fieldID) {
          case "pi":
          case "ci":
          case "f":
            break;
          default:
            var fDef = parentClass.GetFieldDefBySaveIndex(fieldID);
            if (!fDef) {
              console.log("ouch");
            }
            fieldID = fDef.GetFieldID();
            break;
        }
      }

      var fieldValue = objField.GetValue();
      switch (fieldID) {
        case "f":
          this.fFiltered = fieldValue === "1";
          break;
        case "pi": // "parentid":
        case "parentid":
          fieldValue = parseInt(fieldValue);
          this.SetTempParentID(fieldValue);
          break;
        case "ci": // "childindex":
        case "childindex":
          fieldValue = parseInt(fieldValue);
          this.SetTempChildIndex(fieldValue);
          break;
        case "ID":
          fieldValue = parseInt(fieldValue);
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          this.AddNewFieldValue(fieldDef, fieldValue);
          fieldDef.SetReadOnly(true);
          break;
        case "Locked":
        case "Persist":
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          fieldValue = parseInt(fieldValue);
          this.AddNewFieldValue(fieldDef, fieldValue);
          break;
        case "Orphan":
        case "TopOfSubChart":
          if (fieldID == "Orphan") plFound = true;
          if (fieldID == "TopOfSubChart") sCTFound = true;
          fieldValue = parseInt(fieldValue);
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          this.AddNewFieldValue(fieldDef, fieldValue);
          fieldDef.SetReadOnly(true);
          break;
        case "AlsoReportsTo":
        case "Also^Reports^To":
          fieldValue = JSON.parse(fieldValue);
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          this.AddNewFieldValue(fieldDef, fieldValue);
          fieldDef.SetReadOnly(true); // why here?
          break;
        default:
          var fieldDef = parentClass.GetFieldDefByID(fieldID);
          var isCompressed = fieldDef.GetCompressed();
          if (isCompressed && fieldDef["lookup"]) {
            var lookup = fieldDef["lookup"];
            var v = lookup.LookupRef(fieldValue);
            this.AddNewFieldValue(fieldDef, v);
          } else this.AddNewFieldValue(fieldDef, fieldValue);
          break;
      }
    }

    if (!plFound) {
      var fieldDef = parentClass.GetFieldDefByID("Orphan");
      this.AddNewFieldValue(fieldDef, 0);
    }

    if (!sCTFound) {
      var fieldDef = parentClass.GetFieldDefByID("TopOfSubChart");
      this.AddNewFieldValue(fieldDef, 0);
    }
  }

  // Hierarchy Management
  SetParent(parentDataObj, childIndex) {
    this.Journal(false);

    var oldParent = this.fParent;
    if (oldParent == parentDataObj && oldParent != null) {
      var currentIndex = parentDataObj.IndexOfChild(this);
      if (childIndex == -1) childIndex = parentDataObj.GetNumChildren() - 1;

      if (currentIndex == childIndex)
        // Same parent, same child index - nothing has changed
        return;

      if (childIndex > currentIndex)
        // Since we are removing the item the childIndex will be one too big
        childIndex = childIndex - 1;

      parentDataObj.RemoveChild(this);
      parentDataObj.AddChildAt(this, childIndex);
      return;
    }

    this.fParent = parentDataObj;
    if (parentDataObj != null) {
      if (childIndex == -1) parentDataObj.AddChild(this);
      else parentDataObj.AddChildAt(this, childIndex);
    }

    if (oldParent != null) oldParent.RemoveChild(this);
  }

  SetAsOrphan(setAsOrphan) {
    //this.Journal(false);
    var isOrphan = 0;
    if (setAsOrphan) isOrphan = 1;

    var fieldDef = this.GetFieldDefByID("Orphan");
    this.SetFieldValue(fieldDef, isOrphan);
    this.SetSubordinateFieldValues(fieldDef, isOrphan);
  }

  AdoptChildren(currentParentDataObj, returnList) {
    this.Journal(false);
    var done = false;
    while (!done) {
      var numChildren = currentParentDataObj.GetNumChildren();
      if (numChildren == 0) done = true;
      else {
        var childObj = currentParentDataObj.GetIndexChild(0);
        childObj.SetParent(this, -1);
        if (returnList && returnList.indexOf(childObj) == -1) returnList.push(childObj);
      }
    }
  }

  GetParent() {
    return this.fParent;
  }

  GetNumChildrenNoFilter() {
    if (this.fChildren == null) return 0;

    return this.GetChildListNoFilter(false).length;
  }

  GetNumChildren() {
    if (this.fChildren == null) return 0;

    var count = 0;
    for (var i = 0; i < this.fChildren.length; i++) {
      if (!this.fChildren[i].IsFiltered()) count++;
    }
    return count;
  }

  GetChildList(createIt) {
    if (this.fChildren == null) {
      if (createIt) this.fChildren = [];
      else return null;
    }

    return this.fChildren.filter(function (a) {
      return !a.IsFiltered();
    });
  }

  GetChildListNoFilter(createIt) {
    if (this.fChildren == null) {
      if (createIt) this.fChildren = [];
      else return null;
    }

    return this.fChildren;
  }

  GetEverySubordinate(filtered = true, createIt = true, result) {
    if (!result) result = [];
    const children = filtered ? this.GetChildList(createIt) : this.GetChildListNoFilter(createIt);

    if (!children) return result;

    for (var i = 0; i < children.length; i++) {
      result.push(children[i]);
      children[i].GetEverySubordinate(filtered, createIt, result);
    }

    return result;
  }

  AddChild(dataObj) {
    this.Journal(false);
    var c = this.GetChildList(true);

    this.fChildren.push(dataObj);
  }

  AddChildAt(dataObj, index) {
    this.Journal(false);
    var c = this.GetChildList(true);

    this.fChildren.splice(index, 0, dataObj);
  }

  GetIndexChild(theIndex) {
    return this.GetChildList(false)[theIndex];
  }

  GetIndexChildNoFilter(theIndex) {
    return this.GetChildListNoFilter(false)[theIndex];
  }

  IndexOfChild(childDataObj) {
    if (this.fChildren == null) return -1;

    var index = this.GetChildList(false).indexOf(childDataObj);
    return index;
  }

  IndexOfChildNoFilter(childDataObj) {
    if (this.fChildren == null) return -1;
    //NOTE - bypasses filtering system...generally do not use.
    var index = this.fChildren.indexOf(childDataObj);
    return index;
  }

  RemoveChild(dataObj) {
    this.Journal(false);

    if (this.fChildren == null) return false;

    var index = this.fChildren.indexOf(dataObj);
    if (index != -1) {
      this.fChildren.splice(index, 1);
      return true;
    }

    return false;
  }

  SwapChildList(swapList) {
    this.Journal(false);
    this.fChildren = swapList;
  }

  IsRoot() {
    var parent = this.GetParent();
    if (parent == null) return true;
    return false;
  }

  InParentChain(targetParent) {
    var parent = this.GetParent();
    if (parent == null) return false;
    else if (parent != targetParent) return parent.InParentChain(targetParent);
    else return true;
  }

  GetRoot() {
    var parent = this.GetParent();
    if (parent == null) return this;
    else return parent.GetRoot(); //limitID);
  }

  GetTempParentID() {
    return this.fTempParentID;
  }

  SetTempParentID(parentID) {
    this.fTempParentID = parentID;
  }

  GetTempChildIndex() {
    return this.fTempChildIndex;
  }

  SetTempChildIndex(childIndex) {
    this.fTempChildIndex = childIndex;
  }

  SetSortKey(sortKey) {
    this.fSortKey = sortKey;
  }

  DoSortChildren() {
    this.Journal(false);
    var myChildren = this.fChildren;
    if (!myChildren || myChildren.length === 0) return [];

    myChildren.sort(function (o, t) {
      return o.fSortKey - t.fSortKey;
    });

    this.fChildren = myChildren;
  }

  SortChildrenOnKeys(sortConfig) {
    this.Journal(false);
    var myChildren = this.fChildren;
    if (!myChildren || myChildren.length === 0) return [];

    var numKeys = sortConfig.fSortList;
    var fieldDefList = [];

    for (var j in sortConfig.fSortList) {
      var criteria = sortConfig.GetIndexCriteria(j);
      var fieldID = criteria.GetFieldID().replace("^", " ");
      var fieldDef = this.GetFieldDefByID(fieldID);
      if (!fieldDef) fieldDef = this.GetFieldDefByName(criteria.GetFieldName());
      if (!fieldDef) continue; //TODO: brittle
      fieldDef["sortascending"] = criteria.GetSortOrder();
      fieldDefList.push(fieldDef);
    }

    this.fChildren = this.fChildren.sort(function (object1, object2) {
      for (var i in fieldDefList) {
        var fieldDef = fieldDefList[i];
        var isNum = fieldDef.GetFieldType() == enumFieldType.number;
        var isDate = fieldDef.GetFieldType() == enumFieldType.date;
        var sortAscending = fieldDef["sortascending"];
        var v1 = object1.GetFieldValue(fieldDef);
        var v2 = object2.GetFieldValue(fieldDef);
        if (isNum) {
          v1 = v1 == "" || v1 == null ? 0 : parseInt(v1);
          v2 = v2 == "" || v2 == null ? 0 : parseInt(v2);
        } else if (isDate) {
          v1 = v1 == "" || v1 == null ? new Date(0) : new Date(v1);
          v2 = v2 == "" || v2 == null ? new Date(0) : new Date(v2);
        }
        if (v1 < v2) return sortAscending ? -1 : 1;
        if (v1 > v2) return sortAscending ? 1 : -1;
      }
      return 0;
    });
  }

  ClearCachedFieldValueByID(fieldID) {
    var theFieldID = this.fParentClass.GetFieldDefByID(fieldID);
    if (theFieldID) {
      var theFormulaRule = theFieldID.GetFormulaRule();
      if (theFormulaRule) theFormulaRule.ResetCache();
    }
  }

  GetAllChildren(childList) {
    var myChildList = this.GetChildList(false);
    for (var i in myChildList) {
      var child = myChildList[i];
      childList.push(child);
      child.GetAllChildren(childList);
    }
  }

  HasConditionallyBlockedFields() {
    const n = this.GetFieldCount();
    for (let i = 0; i < n; i++) {
      const fieldDef = this.GetIndexFieldDef(i);
      const fieldValue = this.GetFieldValue(fieldDef);
      if (fieldValue === kBlockedFieldTag) return true;
    }
    return false;
  }
}
